import { useState, useEffect, useCallback } from 'react'
import {
  Plug, RefreshCw, CheckCircle, XCircle, ExternalLink,
  MessageCircle, Bell, Globe, Database, Rss, Shield,
  Cable, ArrowRight,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

interface Integration {
  name: string
  type: 'webhook' | 'api' | 'rss' | 'database'
  status: 'connected' | 'disconnected' | 'error'
  description: string
  icon: React.ElementType
  endpoint?: string
  lastCheck?: string
  stats?: string
}

export default function IntegrationsPage() {
  const [systemStatus, setSystemStatus] = useState<any>(null)
  const [feedStats, setFeedStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [statusRes, feedsRes] = await Promise.allSettled([
        api.get('/api/system/status'),
        api.get('/api/system/feeds'),
      ])
      if (statusRes.status === 'fulfilled') setSystemStatus(statusRes.value.data)
      if (feedsRes.status === 'fulfilled') setFeedStats(feedsRes.value.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const dbOk = systemStatus?.checks?.database === 'ok'
  const redisOk = systemStatus?.checks?.redis === 'ok'
  const activeFeedCount = feedStats?.active_sources || 0
  const staleFeedCount = feedStats?.stale_sources || 0

  const integrations: Integration[] = [
    {
      name: 'Discord Bot',
      type: 'webhook',
      status: 'connected',
      description: 'Slash commands (/briefing, /status, /cves, /trends) + webhook alerts for critical CVEs and cloud outages',
      icon: MessageCircle,
      endpoint: 'discord.com/api',
      stats: '4 slash commands, webhook alerts',
    },
    {
      name: 'PostgreSQL',
      type: 'database',
      status: dbOk ? 'connected' : 'error',
      description: 'Primary data store — all articles, CVEs, cloud status, trend snapshots persisted',
      icon: Database,
      endpoint: 'localhost:5450',
      stats: `${feedStats?.feeds?.reduce((s: number, f: any) => s + f.total, 0) || '—'} total articles`,
    },
    {
      name: 'Redis Cache',
      type: 'database',
      status: redisOk ? 'connected' : 'error',
      description: 'Caching layer — API responses cached 1-30 minutes, Celery message broker',
      icon: Database,
      endpoint: 'localhost:6391',
    },
    {
      name: 'NVD API 2.0',
      type: 'api',
      status: 'connected',
      description: 'National Vulnerability Database — CVE feed with CVSS scores, affected products',
      icon: Shield,
      endpoint: 'services.nvd.nist.gov',
      stats: 'Hourly ingestion',
    },
    {
      name: 'CISA KEV',
      type: 'api',
      status: 'connected',
      description: 'Known Exploited Vulnerabilities catalog — cross-referenced with NVD CVEs',
      icon: Shield,
      endpoint: 'cisa.gov',
      stats: 'Hourly ingestion',
    },
    {
      name: 'RSS Mega Feed',
      type: 'rss',
      status: activeFeedCount > 0 ? 'connected' : 'disconnected',
      description: `${activeFeedCount + staleFeedCount} RSS sources across 14 categories — tech news, AI, cyber, cloud, startups, etc.`,
      icon: Rss,
      stats: `${activeFeedCount} active, ${staleFeedCount} stale`,
    },
    {
      name: 'GitHub Search API',
      type: 'api',
      status: 'connected',
      description: 'Trending repositories — stars, forks, language breakdown',
      icon: Globe,
      endpoint: 'api.github.com',
    },
    {
      name: 'ArXiv API',
      type: 'api',
      status: 'connected',
      description: 'CS/AI research papers — latest submissions in AI, ML, NLP',
      icon: Globe,
      endpoint: 'export.arxiv.org',
    },
    {
      name: 'USGS Earthquake API',
      type: 'api',
      status: 'connected',
      description: 'Real-time earthquake data — M4.5+ events worldwide with tech impact analysis',
      icon: Globe,
      endpoint: 'earthquake.usgs.gov',
    },
    {
      name: 'Submarine Cable Map',
      type: 'api',
      status: 'connected',
      description: '693 submarine cables with landing point coordinates and health correlation',
      icon: Globe,
      endpoint: 'submarinecablemap.com',
    },
    {
      name: 'Cloud Status Feeds',
      type: 'rss',
      status: 'connected',
      description: 'AWS, Azure, GCP, M365 status RSS + 8 secondary providers',
      icon: Globe,
      stats: '19 providers monitored',
    },
    {
      name: 'Hacker News API',
      type: 'api',
      status: 'connected',
      description: 'Top stories + Who\'s Hiring threads via Firebase/Algolia',
      icon: Globe,
      endpoint: 'hacker-news.firebaseio.com',
    },
  ]

  const STATUS_STYLES: Record<string, { dot: string; text: string }> = {
    connected: { dot: 'bg-emerald', text: 'text-emerald-light' },
    disconnected: { dot: 'bg-amber', text: 'text-amber-light' },
    error: { dot: 'bg-rose', text: 'text-rose-light' },
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Pointer to the editable Connectors page — this page is read-only RSS / system status */}
      <div className="card p-3 bg-blue/5 border border-blue/30 flex items-center gap-3">
        <Cable size={18} className="text-blue shrink-0" />
        <div className="flex-1">
          <div className="text-[12px] font-semibold text-text-primary">Looking to add or edit a commercial-tool connector?</div>
          <div className="text-[10px] text-text-secondary">
            This page shows RSS feed / system integration status (read-only). To configure CrowdStrike, BMC Helix,
            SCCM, Defender, Azure, Splunk and 30+ others — and to edit existing ones — use the Connectors manager.
          </div>
        </div>
        <Link to="/app/connectors-manage" className="btn-primary text-xs inline-flex items-center gap-1 shrink-0">
          Open Connectors <ArrowRight size={12} />
        </Link>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Plug size={20} className="text-purple" /> Integrations & RSS feeds (read-only)
          </h1>
          <p className="text-xs text-text-secondary">
            {integrations.filter(i => i.status === 'connected').length} connected, {activeFeedCount} active RSS feeds
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* System health */}
      <div className="grid grid-cols-3 gap-3">
        <div className={clsx('card p-3 border', dbOk ? 'border-emerald/30 bg-emerald/5' : 'border-rose/30 bg-rose/5')}>
          <div className="flex items-center gap-2">
            {dbOk ? <CheckCircle size={16} className="text-emerald" /> : <XCircle size={16} className="text-rose" />}
            <span className="text-xs font-medium text-text-primary">PostgreSQL</span>
            <span className={clsx('text-[10px] ml-auto', dbOk ? 'text-emerald' : 'text-rose')}>{dbOk ? 'OK' : 'Error'}</span>
          </div>
        </div>
        <div className={clsx('card p-3 border', redisOk ? 'border-emerald/30 bg-emerald/5' : 'border-rose/30 bg-rose/5')}>
          <div className="flex items-center gap-2">
            {redisOk ? <CheckCircle size={16} className="text-emerald" /> : <XCircle size={16} className="text-rose" />}
            <span className="text-xs font-medium text-text-primary">Redis</span>
            <span className={clsx('text-[10px] ml-auto', redisOk ? 'text-emerald' : 'text-rose')}>{redisOk ? 'OK' : 'Error'}</span>
          </div>
        </div>
        <div className="card p-3 border border-emerald/30 bg-emerald/5">
          <div className="flex items-center gap-2">
            <Rss size={16} className="text-emerald" />
            <span className="text-xs font-medium text-text-primary">Feeds</span>
            <span className="text-[10px] text-emerald ml-auto">{activeFeedCount} active</span>
          </div>
        </div>
      </div>

      {/* Integration cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {integrations.map(int => {
          const s = STATUS_STYLES[int.status]
          return (
            <div key={int.name} className="card p-4 hover:border-border-light transition-colors">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-surface-hover">
                  <int.icon size={16} className="text-text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-text-primary">{int.name}</span>
                    <span className={clsx('w-2 h-2 rounded-full', s.dot)} />
                    <span className={clsx('text-[10px]', s.text)}>{int.status}</span>
                    <span className="text-[9px] text-text-muted bg-surface-hover px-1.5 py-0.5 rounded ml-auto">{int.type}</span>
                  </div>
                  <p className="text-[11px] text-text-secondary leading-relaxed">{int.description}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-text-muted">
                    {int.endpoint && <span className="font-mono">{int.endpoint}</span>}
                    {int.stats && <span>{int.stats}</span>}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Feed source breakdown */}
      {feedStats?.feeds && (
        <div className="card p-4">
          <h2 className="text-xs font-semibold text-text-primary mb-3">RSS Feed Sources ({feedStats.feeds.length})</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 max-h-[400px] overflow-y-auto">
            {feedStats.feeds.map((f: any, i: number) => (
              <div key={i} className="flex items-center gap-2 py-1 text-[10px]">
                <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', f.health === 'active' ? 'bg-emerald' : f.health === 'stale' ? 'bg-amber' : 'bg-rose')} />
                <span className="text-text-primary truncate flex-1">{f.source}</span>
                <span className="text-text-muted font-mono">{f.last_24h}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
